import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, UserPlus, X, Camera, Plus } from 'lucide-react';
import { useWhatsApp } from '../context/WhatsAppContext';

const Sidebar = ({ isMobileOpen, onMobileClose, onStatusClick, onPostStatus }) => {
  const { contacts, activeChat, selectContact, messages, unreadCounts, statuses, myStatus } = useWhatsApp();
  const [search, setSearch] = useState('');
  const [openAdd, setOpenAdd] = useState(false);
  const [newNumber, setNewNumber] = useState('');
  const [profilePics, setProfilePics] = useState({});
  const fetchedJids = useRef(new Set());

  const chatContacts = useMemo(() => {
    return contacts.filter(c => !c.jid?.includes('status') && !c.jid?.includes('broadcast'));
  }, [contacts]);

  useEffect(() => {
    chatContacts.forEach((contact) => {
      const { jid } = contact;
      if (!jid || fetchedJids.current.has(jid)) return;
      fetchedJids.current.add(jid);
      fetch(`http://localhost:5001/profile-pic/${jid}`)
        .then(res => res.json())
        .then(data => setProfilePics(prev => ({ ...prev, [jid]: data.url || null })))
        .catch(() => setProfilePics(prev => ({ ...prev, [jid]: null })));
    });
  }, [chatContacts]);

  const filteredContacts = useMemo(() => {
    let list = chatContacts;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(c => (c.name || c.jid).toLowerCase().includes(q));
    }
    list = [...list].sort((a, b) => {
      const lastA = messages[a.jid]?.slice(-1)[0];
      const lastB = messages[b.jid]?.slice(-1)[0];
      return (lastB?.timestamp || 0) - (lastA?.timestamp || 0);
    });
    return list;
  }, [chatContacts, search, messages]);

  const handleAddNewChat = () => {
    if (!newNumber.trim()) return;
    const jid = `${newNumber.trim()}@s.whatsapp.net`;
    selectContact(jid);
    setOpenAdd(false);
    setNewNumber('');
    setSearch('');
    if (onMobileClose) onMobileClose();
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-[#f0f2f5]">
      {onMobileClose && (
        <div className="md:hidden absolute top-4 right-4 z-10">
          <button onClick={onMobileClose} className="p-1 rounded-full hover:bg-gray-200">
            <X size={20} />
          </button>
        </div>
      )}

      <div className="px-4 py-3 flex items-center gap-2 bg-[#f0f2f5]">
        <div className="flex-1 relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Search size={18} /></span>
          <input
            type="text" placeholder="Search or start new chat" value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-8 py-2 bg-white rounded-lg text-sm outline-none focus:ring-2 focus:ring-green-300"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
              <X size={16} />
            </button>
          )}
        </div>
        <button onClick={() => setOpenAdd(true)} className="p-2 bg-white rounded-lg text-gray-600 hover:bg-gray-200">
          <UserPlus size={18} />
        </button>
      </div>

      <div className="h-px bg-gray-300" />

      {/* STATUS SECTION */}
      <div className="px-4 py-2 bg-white border-b border-gray-200">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</h3>
        <div className="flex items-center gap-3 mt-2 overflow-x-auto no-scrollbar">
          {/* My Status */}
          <button
            onClick={() => {
              if (myStatus && myStatus.media?.length > 0) {
                onStatusClick?.('my-status');
              } else {
                onPostStatus?.();
              }
            }}
            className="flex flex-col items-center gap-1 flex-shrink-0"
          >
            <div className="relative w-14 h-14">
              {myStatus?.media?.length > 0 ? (
                <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-yellow-400 via-green-400 to-blue-500 p-[2px]">
                  <div className="w-full h-full rounded-full bg-white p-[2px]">
                    {myStatus.media[0].type === 'image' ? (
                      <img src={myStatus.media[0].url} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <div className="w-full h-full rounded-full bg-[#25d366] flex items-center justify-center">
                        <Camera size={20} className="text-white" />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-yellow-400 via-green-400 to-blue-500 p-[2px]">
                    <div className="w-full h-full rounded-full bg-white p-[2px]">
                      <div className="w-full h-full rounded-full bg-[#25d366] flex items-center justify-center">
                        <Camera size={20} className="text-white" />
                      </div>
                    </div>
                  </div>
                  <div className="absolute bottom-0 right-0 w-6 h-6 bg-[#075e54] rounded-full flex items-center justify-center">
                    <Plus size={14} className="text-white" />
                  </div>
                </>
              )}
            </div>
            <span className="text-xs text-gray-600 whitespace-nowrap">{myStatus ? 'My Status' : 'Add Status'}</span>
          </button>

          {/* Friends' statuses */}
          {statuses
            .filter(s => s.id !== 'my-status')
            .map(s => (
              <button
                key={s.id}
                onClick={() => onStatusClick?.(s.id)}
                className="flex flex-col items-center gap-1 flex-shrink-0"
              >
                <div className="w-14 h-14 rounded-full bg-gradient-to-tr from-yellow-400 via-green-400 to-blue-500 p-[2px]">
                  <div className="w-full h-full rounded-full bg-white p-[2px]">
                    {s.media[0]?.type === 'image' ? (
                      <img src={s.media[0].url} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gray-300 flex items-center justify-center text-lg font-semibold text-gray-600">
                        {s.userName?.[0]?.toUpperCase()}
                      </div>
                    )}
                  </div>
                </div>
                <span className="text-xs text-gray-600 whitespace-nowrap">{s.userName}</span>
              </button>
            ))}
        </div>
      </div>
      <div className="h-px bg-gray-300" />
      {/* END STATUS */}

      <div className="flex-1 overflow-y-auto">
        {filteredContacts.map((contact) => {
          const lastMsg = messages[contact.jid]?.slice(-1)[0];
          const unread = unreadCounts[contact.jid] || 0;
          const isActive = activeChat === contact.jid;
          const picUrl = profilePics[contact.jid];

          return (
            <div
              key={contact.jid}
              onClick={() => {
                selectContact(contact.jid);
                if (onMobileClose) onMobileClose();
              }}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors duration-150 ${
                isActive ? 'bg-[#e9edef] hover:bg-[#dde1e4]' : 'hover:bg-[#f5f6f6]'
              }`}
            >
              <div className="relative flex-shrink-0">
                {picUrl ? (
                  <img src={picUrl} alt="" className="w-11 h-11 rounded-full object-cover" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#25d366] text-white flex items-center justify-center font-semibold text-lg">
                    {(contact.name || contact.jid)[0]?.toUpperCase()}
                  </div>
                )}
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center">
                  <h3 className={`text-sm font-medium truncate ${isActive ? 'font-bold' : ''}`}>
                    {contact.name || contact.jid.split('@')[0]}
                  </h3>
                  {lastMsg && (
                    <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">
                      {new Date(lastMsg.timestamp * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate mt-0.5">
                  {lastMsg ? (lastMsg.isMine ? 'You: ' : '') + lastMsg.text : contact.jid.includes('@g.us') ? 'Group chat' : 'Tap to chat'}
                </p>
              </div>
              {unread > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">{unread}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <div className="hidden md:block fixed top-0 left-0 h-full w-[350px] bg-[#f0f2f5] border-r border-gray-300 z-20 pt-16">
        {sidebarContent}
      </div>

      {isMobileOpen && (
        <div className="md:hidden fixed inset-0 z-30">
          <div className="absolute inset-0 bg-black/40" onClick={onMobileClose} />
          <div className="absolute top-0 left-0 h-full w-[85%] max-w-[350px] bg-[#f0f2f5] border-r border-gray-300 shadow-xl pt-16">
            {sidebarContent}
          </div>
        </div>
      )}

      {openAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4">Start new chat</h3>
            <input
              type="text" autoFocus placeholder="Phone number (with country code)"
              value={newNumber} onChange={(e) => setNewNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddNewChat()}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-green-300 mb-4"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpenAdd(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md text-sm">Cancel</button>
              <button onClick={handleAddNewChat} className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-semibold hover:bg-green-700">Start Chat</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;